import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api';
import useLatestOrgId from './useLatestOrgId';

const PACKAGE_COLORS = {
  starter:      { bg: '#f0fdf4', border: '#86efac', badge: '#16a34a', text: '#15803d' },
  standard:     { bg: '#eff6ff', border: '#93c5fd', badge: '#2563eb', text: '#1d4ed8' },
  professional: { bg: '#faf5ff', border: '#d8b4fe', badge: '#7c3aed', text: '#6d28d9' },
  enterprise:   { bg: '#fff7ed', border: '#fbbf24', badge: '#d97706', text: '#92400e' },
};

const TXN_COLORS = {
  PURCHASE:       { bg: '#f0fdf4', color: '#15803d', icon: 'fa-shopping-cart' },
  ADMIN_GRANT:    { bg: '#eff6ff', color: '#1d4ed8', icon: 'fa-gift' },
  ADMIN_DISCOUNT: { bg: '#faf5ff', color: '#6d28d9', icon: 'fa-tag' },
  USAGE:          { bg: '#fef2f2', color: '#dc2626', icon: 'fa-bolt' },
  REFUND:         { bg: '#f0fdf4', color: '#059669', icon: 'fa-undo' },
};

export default function CreditsTab({ orgId }) {
  const { t } = useTranslation();
  const latestOrgIdRef = useLatestOrgId(orgId);
  const OP_COSTS = [
    { key: 'CONNECTOR_SYNC', label: t('CreditsTab.opConnectorSync'), cost: 5, icon: 'fa-sync', color: '#2563eb' },
    { key: 'MANUAL_AUDIT',   label: t('CreditsTab.opManualAudit'),   cost: 3, icon: 'fa-shield-alt', color: '#7c3aed' },
    { key: 'BAS_AWS',        label: t('CreditsTab.opBasAws'),        cost: 15, icon: 'fa-cloud', color: '#dc2626' },
    { key: 'BAS_SSH',        label: t('CreditsTab.opBasSsh'),        cost: 10, icon: 'fa-terminal', color: '#ea580c' },
    { key: 'ATTACK_PATH_SIM',label: t('CreditsTab.opAttackPathSim'), cost: 2, icon: 'fa-project-diagram', color: '#0891b2' },
  ];
  const [balance, setBalance]     = useState(null);
  const [packages, setPackages]   = useState([]);
  const [packagesLoaded, setPackagesLoaded] = useState(false);
  const [txns, setTxns]           = useState([]);
  const [txnsLoaded, setTxnsLoaded] = useState(false);
  const [totalTxns, setTotalTxns] = useState(0);
  const [buyingIds, setBuyingIds] = useState(() => new Set());
  const [buyError, setBuyError]   = useState(null);

  const loadBalance = useCallback(() => {
    const params = orgId ? { orgId } : {};
    setBalance(null);
    axios.get('/iasm/credits/balance', { params })
      .then(r => {
        if (latestOrgIdRef.current !== orgId) return; // org switched while this request was in flight
        setBalance(r.data);
      })
      .catch(() => {});
  }, [orgId, latestOrgIdRef]);

  const loadTxns = useCallback(() => {
    const params = { page: 0, size: 20 };
    if (orgId) params.orgId = orgId;
    setTxnsLoaded(false);
    axios.get('/iasm/credits/transactions', { params })
      .then(r => {
        if (latestOrgIdRef.current !== orgId) return; // org switched while this request was in flight
        setTxns(r.data.content || []);
        setTotalTxns(r.data.totalElements || 0);
      })
      .catch(() => {})
      .finally(() => { if (latestOrgIdRef.current === orgId) setTxnsLoaded(true); });
  }, [orgId, latestOrgIdRef]);

  useEffect(() => {
    axios.get('/iasm/credits/packages')
      .then(r => setPackages(r.data))
      .catch(() => {})
      .finally(() => setPackagesLoaded(true));
  }, []);

  useEffect(() => { loadBalance(); loadTxns(); }, [loadBalance, loadTxns]);

  // Check for Stripe redirect back
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    if (params.get('credits') === 'purchased') {
      loadBalance();
      loadTxns();
      window.location.hash = window.location.hash.split('?')[0];
    }
  }, [loadBalance, loadTxns]);

  // buyPackage's success path navigates away (window.location.href = checkoutUrl) without ever
  // clearing buyingIds -- if the browser restores this page from bfcache (e.g. user hits Back
  // from Stripe checkout), React's frozen state snapshot can still show a stale in-flight
  // "Redirecting..." button. event.persisted === true means this pageshow came from bfcache, not
  // a fresh load, so it's safe to reset -- a fresh load already starts with an empty Set.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) setBuyingIds(new Set());
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const buyPackage = (pkg) => {
    setBuyingIds(prev => new Set(prev).add(pkg.id));
    setBuyError(null);
    const params = orgId ? { orgId } : {};
    const buyOrgId = orgId;
    axios.post('/iasm/credits/checkout', { packageId: pkg.id }, { params })
      .then(r => {
        if (latestOrgIdRef.current !== buyOrgId) return; // org switched while this request was in flight -- abandon the stale-org checkout
        window.location.href = r.data.checkoutUrl;
      })
      .catch(e => {
        if (latestOrgIdRef.current !== buyOrgId) return;
        setBuyError(e?.response?.data?.message || t('CreditsTab.paymentInitFailed'));
      })
      .finally(() => setBuyingIds(prev => { // always clear this id, even for a stale org or post-redirect return -- pageshow heal is belt-and-braces
        const next = new Set(prev);
        next.delete(pkg.id);
        return next;
      }));
  };

  const credits = balance?.credits ?? 0;

  return (
    <div>
      {/* Balance hero */}
      <div style={{
        background: balance === null || credits > 0 ? 'linear-gradient(135deg, #1e40af, #4f46e5)' : 'linear-gradient(135deg, #7f1d1d, #dc2626)',
        borderRadius: '12px', padding: '28px 32px', marginBottom: '28px', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px',
      }}>
        <div>
          <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {t('CreditsTab.creditBalance')}
          </div>
          <div style={{ fontSize: '52px', fontWeight: 800, lineHeight: 1 }}>
            {balance === null ? '—' : credits}
          </div>
          <div style={{ fontSize: '14px', opacity: 0.7, marginTop: '6px' }}>
            {balance === null
              ? ' '
              : credits < 0
              ? t('CreditsTab.graceUsed')
              : credits === 0
              ? t('CreditsTab.noCreditsGrace')
              : t('CreditsTab.creditsAvailable')}
          </div>
        </div>
        <div style={{ fontSize: '12px', opacity: 0.75, maxWidth: '280px', lineHeight: '1.8' }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{t('CreditsTab.whatsFree')}</div>
          <div>{t('CreditsTab.freeConnectors')}</div>
          <div>{t('CreditsTab.freeAssets')}</div>
          <div>{t('CreditsTab.freeFindings')}</div>
          <div style={{ marginTop: '8px', fontWeight: 600 }}>{t('CreditsTab.requiresCredits')}</div>
          <div>{t('CreditsTab.creditCostsSummary')}</div>
        </div>
      </div>

      {buyError && (
        <div style={{ marginBottom: '16px', padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '13px', color: '#991b1b' }}>
          <i className="fas fa-times-circle" style={{ marginRight: '6px' }} />{buyError}
        </div>
      )}

      {/* Packages */}
      <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: 600, color: '#111827' }}>
        <i className="fas fa-shopping-cart" style={{ marginRight: '8px', color: '#2563eb' }} />{t('CreditsTab.purchaseCredits')}
      </h3>
      {!packagesLoaded ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', marginBottom: '32px' }}>
          <i className="fas fa-spinner fa-spin" />
        </div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px', marginBottom: '32px' }}>
        {packages.map(pkg => {
          const c = PACKAGE_COLORS[pkg.id] || PACKAGE_COLORS.starter;
          const perCredit = (pkg.priceCents / pkg.credits / 100).toFixed(2);
          return (
            <div key={pkg.id} style={{
              background: c.bg, border: `2px solid ${c.border}`, borderRadius: '10px',
              padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '15px', color: '#111827' }}>{pkg.name}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{pkg.tagline}</div>
                </div>
                <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: c.badge, color: '#fff' }}>
                  CA${(pkg.priceCents / 100).toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 800, color: c.text, lineHeight: 1 }}>
                {pkg.credits}
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', marginLeft: '4px' }}>{t('CreditsTab.creditsLabel')}</span>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                CA${(pkg.priceCents / 100).toFixed(2)} · {t('CreditsTab.perCredit', { amount: perCredit })}
              </div>
              <button
                onClick={() => buyPackage(pkg)}
                disabled={buyingIds.has(pkg.id)}
                style={{
                  marginTop: 'auto', padding: '8px', background: c.badge, color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: buyingIds.has(pkg.id) ? 'default' : 'pointer',
                  fontSize: '13px', fontWeight: 600, opacity: buyingIds.has(pkg.id) ? 0.7 : 1,
                }}
              >
                {buyingIds.has(pkg.id)
                  ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: '5px' }} />{t('CreditsTab.redirecting')}</>
                  : t('CreditsTab.buyFor', { amount: (pkg.priceCents / 100).toFixed(2) })}
              </button>
            </div>
          );
        })}
      </div>
      )}

      {/* Operation costs reference */}
      <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: 600, color: '#111827' }}>
        <i className="fas fa-bolt" style={{ marginRight: '8px', color: '#d97706' }} />{t('CreditsTab.creditCostsPerOperation')}
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px', marginBottom: '32px' }}>
        {OP_COSTS.map(op => (
          <div key={op.key} style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '12px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
          }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: op.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className={`fas ${op.icon}`} style={{ color: op.color, fontSize: '15px' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>{op.label}</div>
            </div>
            <div style={{ fontWeight: 800, fontSize: '18px', color: op.color, flexShrink: 0 }}>
              {op.cost}
              <span style={{ fontSize: '10px', fontWeight: 500, color: '#9ca3af', marginLeft: '2px' }}>{t('CreditsTab.creditsAbbrev')}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Transaction history */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111827' }}>
          <i className="fas fa-history" style={{ marginRight: '8px', color: '#6b7280' }} />{t('CreditsTab.transactionHistory')}
        </h3>
        <span style={{ fontSize: '12px', color: '#9ca3af' }}>{t('CreditsTab.transactionCount', { count: totalTxns })}</span>
      </div>

      {!txnsLoaded ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
          <i className="fas fa-spinner fa-spin" />
        </div>
      ) : txns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}>
          {t('CreditsTab.noTransactions')}
        </div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[t('CreditsTab.colWhen'), t('CreditsTab.colType'), t('CreditsTab.colCredits'), t('CreditsTab.colDescription')].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txns.map(t => {
                const s = TXN_COLORS[t.type] || TXN_COLORS.USAGE;
                return (
                  <tr key={t.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 14px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {new Date(t.createdAt).toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        background: s.bg, color: s.color, padding: '2px 8px',
                        borderRadius: '4px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
                      }}>
                        <i className={`fas ${s.icon}`} style={{ marginRight: '4px' }} />
                        {t.type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: t.delta > 0 ? '#15803d' : '#dc2626' }}>
                      {t.delta > 0 ? '+' : ''}{t.delta}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>{t.description}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
